import React from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip.tsx'
import { 
  Eye, 
  Edit, 
  Trash2,
  Share2,
  ArrowRight,
  Link as LinkIcon,
  CheckCircle2,
  RotateCcw
} from 'lucide-react'
import { Link as WouterLink } from 'wouter'
import { useToast } from '@/hooks/use-toast.ts'
import {Project} from "@shared/interfaces/project.interface.ts"
import { calculateProjectProgress, calculateModuleProgress } from '@/utils/projectProgress.ts'


interface ProjectCardProps {
  project: Project
  onEdit: (project: Project) => void
  onViewProject: (projectId: string) => void
  onDelete: (project: Project) => void
  isSuperAdmin: boolean
}


export default function ProjectCard({ project, onEdit, onViewProject, onDelete, isSuperAdmin }: ProjectCardProps) {
  const { toast } = useToast()

  const getOverallCompletion = () => {
    // Use shared utility function for consistent progress calculation
    return calculateProjectProgress(project)
  }

  const handleShareWithClient = async (project: Project) => {
    try {
      // Request or refresh a public token for this project
      const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null
      const res = await fetch(`/api/projects/${project.id}/generate-public-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ expires_in_days: 365 * 10 }) // effectively permanent
      })

      if (!res.ok) {
        throw new Error('Failed to generate public link')
      }

      const data = await res.json() as { token: string; publicUrl: string }
      const publicInputUrl = `${window.location.origin}/value-assessment/${project.id}/public-intro?t=${data.token}`

      // Copy to clipboard
      await navigator.clipboard.writeText(publicInputUrl)

      toast({
        title: 'Client Link Copied!',
        description: `Share this link with ${project.client_org || project.name} to collect their data.`,
      })
    } catch (err) {
      console.error('Error creating client share link:', err)
      toast({
        title: 'Link Creation Failed',
        description: 'Could not create public link. Please try again.',
        variant: 'destructive'
      })
    }
  }

  const renderModuleRow = (moduleName: string, completion: number, projectId: string) => {
    const isCompleted = completion >= 100
    const isInProgress = completion > 0 && completion < 100
    const isNotStarted = completion === 0

    const getPillClasses = () => {
      if (isCompleted) return "min-w-[130px] px-3 py-1 rounded-full text-sm text-center bg-done text-white font-medium"
      if (isInProgress) return "min-w-[130px] px-3 py-1 rounded-full text-sm text-center bg-teal-500 text-white dark:text-forestBlack dark:bg-[orange] font-medium"
      return "min-w-[130px] px-3 py-1 rounded-full text-sm text-center border border-gray-500 text-gray-300 font-medium"
    }

    return (
      <div key={moduleName} className="flex justify-between items-center py-1 px-4">
        {/* Unified status pill */}
        <span className={getPillClasses()}>
          {moduleName}
        </span>

        {/* Action icons */}
        <div className="ml-2 flex items-center space-x-2">
          {/* Share icon - only for Maturity when Not Started */}
          {moduleName === 'Maturity Assessment' && isNotStarted && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="text-gray-400 hover:text-white transition-colors"
                  onClick={() => handleShare(projectId, moduleName)}
                >
                  <LinkIcon className="h-5 w-5" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="z-50">
                <p>Share</p>
              </TooltipContent>
            </Tooltip>
          )}

          {/* View and Review icons for completed modules */}
          {isCompleted && (
            <>
              <Tooltip>
                <TooltipTrigger asChild>
                  <WouterLink to={getStartRoute(moduleName, projectId)}>
                    <button className="text-gray-400 hover:text-teal-400 transition-colors"
                            data-cy={`redo${moduleName.replace(/\s+/g, '')}`}>
                      <RotateCcw className="h-5 w-5" />
                    </button>
                  </WouterLink>
                </TooltipTrigger>
                <TooltipContent className="z-50">
                  <p>Review Data Entry</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <WouterLink to={getResultsRoute(moduleName, projectId)}>
                    <button className="text-gray-400 hover:text-white transition-colors"
                            data-cy={`view${moduleName.replace(/\s+/g, '')}`}>
                      <Eye className="h-5 w-5" />
                    </button>
                  </WouterLink>
                </TooltipTrigger>
                <TooltipContent className="z-50">
                  <p>View Summary</p>
                </TooltipContent>
              </Tooltip>
            </>
          )}

          {/* ArrowRight icon for the Not Started or In Progress */}
          {!isCompleted && (
              <Tooltip>
                  <TooltipTrigger asChild>
                      <WouterLink to={getStartRoute(moduleName, projectId)}>
                          <button className="text-gray-400 hover:text-white transition-colors"
                                  data-cy={`visit${moduleName.replace(/\s+/g, '')}`}>
                              <ArrowRight className="h-5 w-5" />
                          </button>
                      </WouterLink>
                  </TooltipTrigger>
                  <TooltipContent className="z-50">
                      <p>Start</p>
                  </TooltipContent>
              </Tooltip>

          )}
        </div>
      </div>
    )
  }

  const getResultsRoute = (moduleName: string, projectId: string) => {
    switch (moduleName) {
      case 'Value Assessment':
        return `/value-assessment/${projectId}/value-assessment-summary`
    case 'Maturity Assessment':
        return `/projects/${projectId}/maturity-results`
      default:
        return '/'
    }
  }

  const getStartRoute = (moduleName: string, projectId: string) => {
    switch (moduleName) {
      case 'Value Assessment':
        return `/value-assessment/${projectId}/intro`
      default:
        return `/projects/${projectId}/assessment`
    }
  }

  const handleShare = async (projectId: string, moduleName: string) => {
    try {
      // Generate or refresh a public token for this project
      const token = typeof window !== 'undefined' ? window.localStorage.getItem('jwt_token') : null
      const res = await fetch(`/api/projects/${projectId}/generate-public-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ expires_in_days: 365 * 10 })
      })

      if (!res.ok) throw new Error('Failed to generate public link')
      const data = await res.json() as { token: string; publicUrl: string }

      let shareUrl = ''
      if (moduleName === 'Maturity Assessment') {
        shareUrl = `${window.location.origin}/projects/${projectId}/assessment?t=${data.token}`
      } else if (moduleName === 'Value Assessment') {
        shareUrl = `${window.location.origin}/value-assessment/${projectId}/public-intro?t=${data.token}`
      } else {
        shareUrl = `${window.location.origin}/project/${projectId}?t=${data.token}`
      }

      await navigator.clipboard.writeText(shareUrl)
      toast({
        title: 'Link Copied',
        description: `Shareable link for ${moduleName} copied to clipboard`
      })
    } catch (err) {
      console.error('Error creating share link:', err)
      toast({
        title: 'Link Creation Failed',
        description: 'Could not create public link. Please try again.',
        variant: 'destructive'
      })
    }
  }

  return (
    <TooltipProvider>
      <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/80 border-2 border-teal-500/30 rounded-2xl hover:shadow-2xl hover:shadow-teal-900/40 hover:z-50 transition-all duration-300 relative">
        {/* Client Submitted Badge - secondary badge if both conditions exist */}
        {project.summary_data?.valueSummary?.meta?.externalCompleted && getOverallCompletion() < 100 && (
          <div className="absolute -top-2 -right-2 z-20">
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full shadow-lg border-2 border-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
              </TooltipTrigger>
              <TooltipContent className="z-50">
                <p>Client has completed this assessment</p>
              </TooltipContent>
            </Tooltip>
          </div>
        )}

        <CardHeader className="pb-4">
          {/* Identity Block */}
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              {/* Client Name - Bold */}
              <CardTitle className="text-xl font-bold text-white line-clamp-1 font-['Archivo']">
                {project.client_org || 'Client Organization'}
              </CardTitle>

              {/* Project Subtitle - Subdued */}
              <CardDescription className="text-base text-gray-400 line-clamp-1 font-['Archivo']">
                {project.name}
              </CardDescription>

              {/* Metadata: Created date and Owner */}
              <div className="flex items-center gap-4 text-sm text-gray-500 mt-2 font-['Archivo']">
                <span>{new Date(project.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}</span>
                <span>Owned by {project.owner_email || 'Admin User'}</span>
              </div>
            </div>

            {/* Action Icons - Top Right */}
            <div className="flex items-center gap-2">
              {/* Share with Client Button */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 dark:hover:bg-green-900/20 text-white hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleShareWithClient(project)}
                  >
                    <Share2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="z-50">
                  <p>Share with Client</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                    data-cy="edit-project"
                    onClick={() => onEdit(project)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="z-50">
                  <p>Edit Project</p>
                </TooltipContent>
              </Tooltip>

              {/* Delete Icon - Only for Super Admins */}
              {isSuperAdmin && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-red-100 dark:hover:bg-red-900/20 text-red-500 hover:text-red-600"
                      data-cy="delete-project"
                      onClick={() => onDelete(project)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent className="z-50">
                    <p>Delete Project</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>


        </CardHeader>

        <CardContent className="space-y-6 px-0 pt-8">
          {/* Centered Progress Ring with consistent spacing */}
          {/*<div className="flex items-center justify-center py-4">*/}
          {/*  <ProgressRing percentage={getOverallCompletion()} />*/}
          {/*</div>*/}

          {/* Modules Section - Stacked Rows - VALUE ASSESSMENT ONLY */}
          <div className="space-y-2">
            {renderModuleRow('Value Assessment', calculateModuleProgress(project, 'value_assessment'), project.id)}
            {renderModuleRow('Maturity Assessment', calculateModuleProgress(project, 'maturity'), project.id)}
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
